import { DepartmentTreeNode } from "@/types/department";
import { AccountBalance as AccountBalanceIcon } from "@mui/icons-material";
import { Box, Checkbox, InputAdornment, Typography } from "@mui/material";
import { useTreeItem } from "@mui/x-tree-view";
import { TreeItem, TreeItemProps } from "@mui/x-tree-view/TreeItem";
import React from "react";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import IconButton from "@mui/material/IconButton";
import TextField from "@mui/material/TextField";

const CleanCheckbox = React.forwardRef<HTMLButtonElement, React.ComponentProps<typeof Checkbox>>((props, ref) => {
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const { visible, ...rest } = props as any;
  return <Checkbox size='small' ref={ref} {...rest} sx={{ p: 0 }} />;
});

interface CustomTreeItemProps extends TreeItemProps {
  onLabelEdit?: (id: string, newLabel: string) => void;
  onDelete?: (id: string) => void;
}

const DemsCustomTreeItem = React.forwardRef(function DemsCustomTreeItem(
  props: CustomTreeItemProps,
  ref: React.Ref<HTMLLIElement>
) {
  const { onDelete, onLabelEdit, ...otherProps } = props;
  const { publicAPI } = useTreeItem(otherProps);
  const data = publicAPI.getItem(otherProps.itemId) as DepartmentTreeNode;
  const [editing, setEditing] = React.useState(false);
  const [editValue, setEditValue] = React.useState(data.label);
  const id = Number(data.id);

  React.useEffect(() => {
    setEditValue(data.label);
  }, [data.label]);

  const handleEditComplete = () => {
    if (editValue.trim() && editValue !== data.label) {
      onLabelEdit?.(data.id.toString(), editValue);
    }
    setEditing(false);
  };

  const labelWithIcon = (
    <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
      {data.isApproval && <AccountBalanceIcon fontSize='small' color='primary' />}
      {editing ? (
        <TextField
          value={editValue}
          inputProps={{ maxLength: 50 }}
          onChange={(e) => setEditValue(e.target.value)}
          onBlur={handleEditComplete}
          onKeyDown={(e) => {
            if (e.key === "Enter") handleEditComplete();
          }}
          size='small'
          autoFocus
          sx={{ width: "100%" }}
          slotProps={{
            input: {
              endAdornment: (
                <InputAdornment position='end'>
                  <span style={{ whiteSpace: "nowrap", fontSize: "0.8rem" }}>{`${editValue?.length ?? 0}/50 자`}</span>
                </InputAdornment>
              ),
            },
          }}
        />
      ) : (
        <>
          <Typography component='span'>{data.label}</Typography>
          {data.isVirtual && id !== 0 && (
            <Box sx={{ display: "flex", gap: 0.5 }}>
              <IconButton
                size='small'
                onClick={(e) => {
                  e.stopPropagation();
                  setEditing(true);
                }}
              >
                <EditIcon fontSize='small' />
              </IconButton>
              <IconButton
                size='small'
                onClick={(e) => {
                  e.stopPropagation();
                  onDelete?.(data.id.toString());
                }}
                color='error'
              >
                <DeleteIcon fontSize='small' />
              </IconButton>
            </Box>
          )}
        </>
      )}
    </Box>
  );

  return (
    <TreeItem
      {...otherProps}
      ref={ref}
      label={labelWithIcon}
      slots={data.isVirtual && id !== 0 ? { checkbox: CleanCheckbox } : undefined}
    />
  );
});

export default DemsCustomTreeItem;
